import { FC, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  Button,
  Flashbar,
  Box,
  Spinner,
  ExpandableSection,
  Badge,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import {
  useGetSessionQuery,
  useSubmitSessionQueryMutation,
  useGetQueryStatusQuery,
  useSubmitSessionFeedbackMutation,
} from 'src/store/apis/uatApi';
import { UatToolCall, UatToolFeedback, UatQueryDetail } from 'src/store/interfaces/Uat.types';
import useNotification from 'src/hooks/useNotification';
import { usePermissions } from 'src/hooks/usePermissions';
import QueryFeedbackEditor from './QueryFeedbackEditor';
import ToolCallCard from './ToolCallCard';
import RawResponseView from './RawResponseView';
import BatchQueryInput from './BatchQueryInput';

// --- Constants ---

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 60000;

// --- Helpers ---

const NOOP = () => {};

const fmtDuration = (ms: number | null): string => (ms != null ? `${(ms / 1000).toFixed(1)}s` : '');

const getQueryDescription = (q: UatQueryDetail): string => {
  if (q.status === 'IN_PROGRESS') return 'Processing…';
  if (q.status === 'FAILED') return 'Failed';
  const ts = new Date(q.submittedTs).toLocaleString();
  const dur = fmtDuration(q.durationMs);
  const feedback = q.feedback ? ' ✓ feedback' : '';
  return `${ts} — ${q.actualTools.length} tool(s)${dur ? ` · ${dur}` : ''}${feedback}`;
};

const getQueryStatusType = (status: string): 'in-progress' | 'error' | 'success' => {
  if (status === 'IN_PROGRESS') return 'in-progress';
  if (status === 'FAILED') return 'error';
  return 'success';
};

const buildEmptyFeedback = (tool: UatToolCall, index: number): UatToolFeedback => ({
  toolIndex: index,
  toolName: tool.toolName,
  vote: null,
  correctedParameters: null,
});

// --- Component ---

const UatSessionDetail: FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { addNotification, notificationItems } = useNotification();
  const { canAccessUat } = usePermissions();
  const { data: session, isLoading, refetch: refetchSession } = useGetSessionQuery(sessionId!, { skip: !sessionId });
  const [submitQuery] = useSubmitSessionQueryMutation();
  const [submitFeedback, { isLoading: isSubmitting }] = useSubmitSessionFeedbackMutation();

  const [batchProgress, setBatchProgress] = useState<{ sent: number; total: number } | null>(null);
  const [pollingQueryId, setPollingQueryId] = useState<string | null>(null);
  const [currentQueryId, setCurrentQueryId] = useState<string | null>(null);
  const [currentTools, setCurrentTools] = useState<UatToolCall[]>([]);
  const [currentRawResponse, setCurrentRawResponse] = useState<string | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const [pollingStartTime, setPollingStartTime] = useState<number | null>(null);
  const [handledPollQueryId, setHandledPollQueryId] = useState<string | null>(null);
  const [editingQueryId, setEditingQueryId] = useState<string | null>(null);

  const { data: pollResult } = useGetQueryStatusQuery(
    { sessionId: sessionId!, queryId: pollingQueryId! },
    {
      skip: !pollingQueryId || !sessionId || pollingTimedOut,
      pollingInterval: pollingQueryId && !pollingTimedOut ? POLL_INTERVAL_MS : 0,
    },
  );

  // Polling timeout
  useEffect(() => {
    if (!pollingQueryId || !pollingStartTime) return;
    const timeoutId = setTimeout(() => {
      setPollingTimedOut(true);
      setPollingQueryId(null);
    }, POLL_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [pollingQueryId, pollingStartTime]);

  // Handle poll result
  useEffect(() => {
    if (!pollResult || pollResult.status === 'IN_PROGRESS') return;
    if (pollResult.queryId === handledPollQueryId) return;
    setHandledPollQueryId(pollResult.queryId);
    setPollingQueryId(null);
    setPollingStartTime(null);
    setPollingTimedOut(false);
    if (pollResult.status === 'COMPLETED') {
      setCurrentQueryId(pollResult.queryId);
      setCurrentTools(pollResult.actualTools);
      setCurrentRawResponse(pollResult.rawResponse);
      refetchSession();
    } else {
      addNotification('error', 'query-error', pollResult.error ?? 'Query processing failed.');
    }
  }, [pollResult, addNotification, refetchSession]);

  // Auto-refresh session when queries are still processing
  useEffect(() => {
    const hasInProgress = session?.queries.some((q) => q.status === 'IN_PROGRESS');
    if (!hasInProgress) return;
    const id = setInterval(() => refetchSession(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [session, refetchSession]);

  const handleBatchSubmit = useCallback(async (queries: string[]) => {
    if (!sessionId) return;
    setBatchProgress({ sent: 0, total: queries.length });
    let failures = 0;
    for (const q of queries) {
      try {
        await submitQuery({ sessionId, body: { userQuery: q } }).unwrap();
      } catch {
        failures++;
      }
      setBatchProgress((prev) => prev && ({ ...prev, sent: prev.sent + 1 }));
    }
    setBatchProgress(null);
    refetchSession();
    if (failures) {
      addNotification('warning', 'batch-partial', `${failures} of ${queries.length} queries failed to submit.`);
    } else {
      addNotification('success', 'batch-success', `${queries.length} queries submitted. Results will appear in Past Queries.`);
    }
  }, [sessionId, submitQuery, addNotification, refetchSession]);

  const handleSubmitFeedback = useCallback(
    async (queryId: string, feedbacks: UatToolFeedback[], comments: string, rawResponseVote: 'up' | 'down' | null) => {
      if (!sessionId) return;
      try {
        await submitFeedback({
          sessionId,
          body: { queryId, toolFeedbacks: feedbacks, rawResponseVote, comments },
        }).unwrap();
        addNotification('success', 'feedback-success', 'Feedback submitted.');
        if (queryId === currentQueryId) {
          setCurrentQueryId(null);
          setCurrentTools([]);
          setCurrentRawResponse(null);
        }
        if (queryId === editingQueryId) {
          setEditingQueryId(null);
        }
        refetchSession();
      } catch {
        addNotification('error', 'feedback-error', 'Failed to submit feedback.');
      }
    },
    [sessionId, currentQueryId, editingQueryId, submitFeedback, addNotification, refetchSession],
  );

  if (isLoading || !session) {
    return <Spinner size="large" />;
  }

  const isProcessing = !!batchProgress || !!pollingQueryId;
  const hasInProgress = session.queries.some((q) => q.status === 'IN_PROGRESS');

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={`Created by ${session.createdBy}`}>
          {session.sessionName}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Flashbar items={notificationItems} />

        {/* Execution Scope */}
        <Container header={<Header variant="h3">Execution Scope</Header>}>
          <SpaceBetween direction="horizontal" size="s">
            <Badge>{session.executionScope.clientLLMType}</Badge>
            <Badge>{session.executionScope.phase}</Badge>
            {session.executionScope.enabledTools.map((t) => (
              <Badge key={t} color="blue">
                {t}
              </Badge>
            ))}
          </SpaceBetween>
        </Container>

        {/* Past Queries */}
        {session.queries.length > 0 && (
          <Container header={
            <Header
              variant="h3"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {hasInProgress && <StatusIndicator type="in-progress">Processing</StatusIndicator>}
                  <Button iconName="refresh" onClick={() => refetchSession()}>Refresh</Button>
                </SpaceBetween>
              }
            >
              Past Queries
            </Header>
          }>
            <SpaceBetween size="m">
              {session.queries.map((q, idx) => (
                <ExpandableSection
                  key={q.queryId}
                  headerText={`${idx + 1}. ${q.userQuery}`}
                  headerDescription={getQueryDescription(q)}
                  headerActions={
                    <StatusIndicator type={getQueryStatusType(q.status)}>{q.status}</StatusIndicator>
                  }
                >
                  <SpaceBetween size="s">
                    {q.status === 'IN_PROGRESS' && (
                      <Box color="text-body-secondary">
                        <Spinner /> Processing your query…
                      </Box>
                    )}

                    {q.status === 'FAILED' && <Box color="text-status-error">Query processing failed.</Box>}

                    {q.status !== 'IN_PROGRESS' && q.status !== 'FAILED' && (
                      <>
                        {q.rawResponse && (
                          <Box padding={{ left: 'l' }}>
                            <SpaceBetween direction="horizontal" size="s">
                              <Box fontSize="heading-s" fontWeight="bold">Raw Response (AskRino)</Box>
                              {q.feedback?.rawResponseVote && (
                                <StatusIndicator type={q.feedback.rawResponseVote === 'up' ? 'success' : 'error'}>
                                  {q.feedback.rawResponseVote === 'up' ? 'Correct' : 'Incorrect'}
                                </StatusIndicator>
                              )}
                            </SpaceBetween>
                            <ExpandableSection
                              headerText={<Box fontSize="body-s">View Response</Box>}
                              defaultExpanded
                            >
                              <RawResponseView content={q.rawResponse} />
                            </ExpandableSection>
                          </Box>
                        )}

                        {editingQueryId === q.queryId ? (
                          <QueryFeedbackEditor
                            tools={q.actualTools}
                            rawResponse={q.rawResponse}
                            initialFeedbacks={q.feedback?.toolFeedbacks ?? null}
                            initialRawResponseVote={q.feedback?.rawResponseVote ?? null}
                            initialComments={q.feedback?.comments ?? ''}
                            onSubmit={(feedbacks, comments, rawResponseVote) =>
                              handleSubmitFeedback(q.queryId, feedbacks, comments, rawResponseVote)
                            }
                            onCancel={() => setEditingQueryId(null)}
                            isSubmitting={isSubmitting}
                          />
                        ) : (
                          <SpaceBetween size="s">
                            {q.actualTools.length > 0 ? (
                              q.actualTools.map((tool, i) => (
                                <ToolCallCard
                                  key={i}
                                  toolCall={tool}
                                  feedback={q.feedback?.toolFeedbacks[i] ?? buildEmptyFeedback(tool, i)}
                                  onFeedbackChange={NOOP}
                                  editing={false}
                                />
                              ))
                            ) : q.feedback?.toolFeedbacks[0] ? (
                              <ToolCallCard
                                toolCall={{ toolName: 'No Tool', toolParameters: {}, status: 'N/A', responseData: null, durationMs: null }}
                                feedback={q.feedback.toolFeedbacks[0]}
                                onFeedbackChange={NOOP}
                                editing={false}
                              />
                            ) : (
                              <Box color="text-body-secondary">No tool calls were made for this query.</Box>
                            )}
                            {q.feedback?.comments && (
                              <Box fontSize="body-s" color="text-body-secondary">
                                Comment: {q.feedback.comments} — {q.feedback.submittedBy}
                              </Box>
                            )}
                            <Button variant="normal" iconName="edit" onClick={() => setEditingQueryId(q.queryId)} disabled={!canAccessUat}>
                              {q.feedback ? 'Edit Feedback' : 'Add Feedback'}
                            </Button>
                          </SpaceBetween>
                        )}
                      </>
                    )}
                  </SpaceBetween>
                </ExpandableSection>
              ))}
            </SpaceBetween>
          </Container>
        )}

        {/* Query Input */}
        <Container header={<Header variant="h3">Ask Questions</Header>}>
          <BatchQueryInput onSubmit={handleBatchSubmit} disabled={isProcessing || !canAccessUat} progress={batchProgress} />
        </Container>

        {/* Timeout warning */}
        {pollingTimedOut && (
          <Container>
            <Box textAlign="center" padding="l">
              <SpaceBetween size="s" alignItems="center">
                <StatusIndicator type="warning">Response is taking longer than expected</StatusIndicator>
                <Box variant="p" color="text-body-secondary">
                  Your query is still being processed in the background. Please refresh the page later to see results.
                </Box>
              </SpaceBetween>
            </Box>
          </Container>
        )}

        {/* Current query feedback */}
        {currentTools.length > 0 && currentQueryId && (
          <Container header={<Header variant="h3">Current Query Result</Header>}>
            <SpaceBetween size="s">
              {currentRawResponse && (
                <Box padding={{ left: 'l' }}>
                  <ExpandableSection
                    headerText={<Box fontSize="heading-s" fontWeight="bold">Raw Response (AskRino)</Box>}
                    defaultExpanded
                  >
                    <RawResponseView content={currentRawResponse} />
                  </ExpandableSection>
                </Box>
              )}
              <QueryFeedbackEditor
                tools={currentTools}
                rawResponse={currentRawResponse}
                onSubmit={(feedbacks, comments, rawResponseVote) => handleSubmitFeedback(currentQueryId, feedbacks, comments, rawResponseVote)}
                onCancel={() => {
                  setCurrentQueryId(null);
                  setCurrentTools([]);
                  setCurrentRawResponse(null);
                }}
                isSubmitting={isSubmitting}
              />
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default UatSessionDetail;
